import axiosInstance from './utils/axiosInstance';

/**
 * Noticias y comunicaciones del portal, ahora servidas por MuniSuite.
 *
 * El contenido dejó de vivir en el backend viejo (Mongo) y pasó a MuniSuite, que expone
 * `/web-management/news` SIN pedir autenticación para leer (usa `optionalAuth`), que es lo que
 * este sitio necesita.
 *
 * TODA la traducción vive acá. Los componentes siguen recibiendo exactamente la misma forma de
 * antes —`titulo`, `imagenes`, `fechaPublicacion`, `_id`—, así que no se tocó ni una pantalla: si
 * mañana cambia el contrato de MuniSuite, se cambia este archivo y nada más.
 *
 * Lo que hay que apuntar del otro lado: `REACT_APP_API_CORE` tiene que ser la API de MuniSuite
 * con el prefijo `/api/v1` (ver `utils/axiosInstance.js`).
 */

/** Una noticia de MuniSuite, con la forma que esperan los componentes de este sitio. */
const aFormaDelPortal = (n) => {
  if (!n) return null;
  // MuniSuite guarda las imágenes en `images` y la principal aparte; el portal espera un arreglo.
  const imagenes = Array.isArray(n.images) && n.images.length > 0
    ? n.images
    : (n.featuredImage ? [n.featuredImage] : []);

  return {
    _id: n.id,
    titulo: n.title ?? '',
    descripcion: n.content ?? n.excerpt ?? '',
    contenido: n.content ?? '',
    imagenes,
    autor: n.authorName ?? '',
    categoria: n.category ?? n.webNewsCategory?.name ?? '',
    destacado: Boolean(n.featured),
    activo: n.status === 'PUBLISHED',
    // Publicadas primero por fecha de publicación; si nunca se publicó, la de creación, que es lo
    // que el portal ordena de más nueva a más vieja.
    fechaPublicacion: n.publishedAt ?? n.createdAt ?? null,
    slug: n.slug ?? null,
  };
};

/** MuniSuite responde `{ success, data }`; el portal espera el arreglo pelado. */
const desenvolver = (respuesta) => {
  const cuerpo = respuesta?.data;
  const datos = Array.isArray(cuerpo) ? cuerpo : cuerpo?.data;
  return Array.isArray(datos) ? datos : [];
};

/**
 * Sólo lo PUBLICADO sale al sitio. MuniSuite devuelve también borradores y archivados, y el
 * portal viejo nunca los recibía porque en Mongo el borrador directamente no existía.
 */
const soloPublicadas = (lista) => lista.filter((n) => n.status === 'PUBLISHED');

const getAllNews = async () => {
  const response = await axiosInstance.get('/web-management/news', {
    params: { tipo: 'NOTICIA' },
  });
  return soloPublicadas(desenvolver(response)).map(aFormaDelPortal);
};

const getAllCommunication = async () => {
  const response = await axiosInstance.get('/web-management/news', {
    params: { tipo: 'COMUNICACION' },
  });
  return soloPublicadas(desenvolver(response)).map(aFormaDelPortal);
};

/**
 * Pide la noticia por su id. Esa lectura es, además, la que MuniSuite cuenta como una vista: por
 * eso el detalle NO se resuelve bajando el listado y filtrando, aunque con 17 noticias daría lo
 * mismo en pantalla. Filtrando, el contador de vistas del panel se quedaría en cero para siempre.
 */
const getNewsById = async (id) => {
  const response = await axiosInstance.get(`/web-management/news/${id}`);
  const cuerpo = response?.data;
  return aFormaDelPortal(cuerpo?.data ?? cuerpo);
};

// ── Productos ──────────────────────────────────────────────────────────────────
//
// Siguen apuntando al backend viejo: MuniSuite no tiene productos y la colección estaba vacía al
// migrar. Se dejan tal cual para no romper nada; cuando se apague el backend viejo, se borran.

const createProduct = async (productData) => {
  const response = await axiosInstance.post('/api/products', productData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

const updateProduct = async (id, data) => {
  const response = await axiosInstance.put(`/api/products/${id}`, data);
  return response.data;
};

const deleteProduct = async (id) => {
  const response = await axiosInstance.delete(`/api/products/${id}`);
  return response.data;
};

const ProductService = {
  createProduct,
  getAllCommunication,
  getNewsById,
  updateProduct,
  deleteProduct,
  getAllNews,
};

export default ProductService;
